import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "@/contexts/SettingsContext";
import Logo from "@/components/Logo";
import Portal from "@/components/Portal";
import { useNavigation, navLinkPath } from "@/hooks/useNavigation";

// Pages where the header starts on a DARK hero — use light nav text when at top
const DARK_HERO_PATHS = ["/", "/disclosures"];
// `/p/the-asiabest-difference` is an exception: it renders the About template
// (light bone background), unlike every other /p/* page which uses the CMS
// dark hero. Without this the nav text stays light-on-light and becomes
// invisible until the user scrolls.
const LIGHT_HERO_CMS_PAGES = new Set(["/p/the-asiabest-difference"]);

export default function Header() {
  const settings = useSettings();
  const { visibleGroups } = useNavigation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);            // mobile overlay
  const [openGroup, setOpenGroup] = useState(null);   // desktop dropdown key
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close menus on route change
  useEffect(() => { setOpen(false); setOpenGroup(null); setMobileExpanded(null); }, [location.pathname]);

  // lock body scroll while mobile overlay is open — defer with rAF so it doesn't jank the toggle animation
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      document.body.style.overflow = open ? "hidden" : "";
    });
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
    };
  }, [open]);

  const onDarkHero =
    (DARK_HERO_PATHS.includes(location.pathname) ||
      location.pathname.startsWith("/p/")) &&
    !LIGHT_HERO_CMS_PAGES.has(location.pathname);
  const lightMode = onDarkHero && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-header" : (onDarkHero ? "bg-transparent" : "glass-header")
      }`}
      onMouseLeave={() => setOpenGroup(null)}
    >
      <div className="container-x flex items-center justify-between py-5">
        {/* LOGO */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group flex-shrink-0">
          <Logo size={40} variant={lightMode ? "dark" : "light"} />
          <div className="leading-tight">
            <div className={`font-serif text-base lg:text-lg xl:text-xl tracking-tight transition-colors ${lightMode ? "text-bone" : "text-emerald"}`}>{settings.company_short_name}</div>
            <div className="overline text-[0.55rem] text-gold">{settings.company_suffix}</div>
          </div>
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden xl:flex items-center gap-1">
          {visibleGroups.map((g) => {
            if (g.direct) {
              return (
                <NavLink
                  key={g.key}
                  to={g.href}
                  data-testid={`nav-${g.key}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-[0.78rem] tracking-wide uppercase font-medium transition-colors ${
                      lightMode
                        ? (isActive ? "text-gold" : "text-bone/80 hover:text-gold")
                        : (isActive ? "text-emerald" : "text-foreground/70 hover:text-emerald")
                    }`
                  }
                >
                  {g.label}
                </NavLink>
              );
            }
            const active = openGroup === g.key;
            return (
              <div
                key={g.key}
                onMouseEnter={() => setOpenGroup(g.key)}
                className="relative"
              >
                <button
                  data-testid={`nav-${g.key}`}
                  onClick={() => setOpenGroup(active ? null : g.key)}
                  className={`px-3 py-2 text-[0.78rem] tracking-wide uppercase font-medium inline-flex items-center gap-1.5 transition-colors ${
                    lightMode
                      ? (active ? "text-gold" : "text-bone/80 hover:text-gold")
                      : (active ? "text-emerald" : "text-foreground/70 hover:text-emerald")
                  }`}
                >
                  {g.label}
                  <ChevronDown className={`w-3 h-3 transition-transform ${active ? "rotate-180 text-gold" : ""}`} />
                </button>

                <AnimatePresence>
                  {active && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-0 top-full pt-3 min-w-[280px]"
                    >
                      <div className="bg-emerald-ink border border-white/10 shadow-2xl py-2 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon to-transparent" />
                        {g.items.map((it, i) => (
                          <Link
                            key={it.slug}
                            to={navLinkPath(it)}
                            data-testid={`nav-${g.key}-${it.slug.replace(/\//g, "")}`}
                            className="block px-5 py-2.5 text-sm text-bone/80 hover:text-neon hover:bg-white/[0.03] transition-colors group"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-mono text-[0.6rem] text-bone/30 group-hover:text-neon/60">{String(i + 1).padStart(2, "0")}</span>
                              <span className="flex-1 truncate">{it.label}</span>
                              <ChevronRight className="w-3.5 h-3.5 text-bone/30 group-hover:text-neon group-hover:translate-x-0.5 transition-all" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* MOBILE TOGGLE */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`xl:hidden p-2 ${lightMode ? "text-bone" : "text-emerald"}`}
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* MOBILE FULL-SCREEN OVERLAY — portaled to document.body so the
          header's backdrop-filter (glass-header) doesn't turn the header into
          a containing block that clamps the drawer's fixed positioning. */}
      <Portal>
        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="xl:hidden fixed inset-0 top-[72px] bg-emerald-ink text-bone z-[60] overflow-y-auto"
              style={{
                willChange: "opacity",
                transform: "translateZ(0)",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative container-x py-8 pb-32">
                {visibleGroups.map((g) => {
                  if (g.direct) {
                    return (
                      <Link
                        key={g.key}
                        to={g.href}
                        data-testid={`mobile-nav-${g.key}`}
                        className="flex items-center justify-between py-5 border-b border-white/10 group"
                        style={{ touchAction: "manipulation" }}
                      >
                        <span className="font-display text-2xl text-bone group-hover:text-neon transition-colors">{g.label}</span>
                        <ArrowUpRight className="w-5 h-5 text-bone/40 group-hover:text-neon transition-colors" />
                      </Link>
                    );
                  }
                  const expanded = mobileExpanded === g.key;
                  return (
                    <div key={g.key} className="border-b border-white/10">
                      <button
                        data-testid={`mobile-nav-${g.key}`}
                        onClick={() => setMobileExpanded(expanded ? null : g.key)}
                        className="w-full flex items-center justify-between py-5 group"
                        style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                        aria-expanded={expanded}
                      >
                        <span className={`font-display text-2xl transition-colors ${expanded ? "text-neon" : "text-bone group-hover:text-neon"}`}>{g.label}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform ${expanded ? "rotate-180 text-neon" : "text-bone/40"}`} />
                      </button>
                      <AnimatePresence initial={false}>
                        {expanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="overflow-hidden"
                          >
                            <div className="pb-4 space-y-px">
                              {g.items.map((it, i) => (
                                <Link
                                  key={it.slug}
                                  to={navLinkPath(it)}
                                  data-testid={`mobile-nav-${g.key}-${it.slug.replace(/\//g, "")}`}
                                  className="flex items-center gap-3 py-3 px-2 text-bone/75 hover:text-neon transition-colors"
                                  style={{ touchAction: "manipulation" }}
                                >
                                  <span className="font-mono text-[0.6rem] text-bone/30 w-6">{String(i + 1).padStart(2, "0")}</span>
                                  <span className="flex-1 text-sm">{it.label}</span>
                                  <ChevronRight className="w-4 h-4 text-bone/20" />
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </header>
  );
}
